import * as React from 'react';
import { SegmentAttributeGroup, SegmentAttributeOperator, SelectedAttribute } from '../../../models/segments';
import * as styles from './attribute-row.module.scss';
import { useBuildSegment } from './useBuildSegment';
import AttributeRowCustomers from './attribute-row-customers';
import AttributeGroup from './attribute-group';
import OperatorSelect from './operator-select';
import Attribute from './attribute';
import AttributeMenu from './attribute-menu';
import useSegmentAttributes from '../../../hooks/useSegmentAttributes';
import SegmentCompositionSelector from './segment-composition-selector';

interface Props {
  /** Value of the attribute select component */
  predicate?: SelectedAttribute | SegmentAttributeGroup;
  /** Whether the attribute select should be marked `required` */
  required?: boolean;
  /** Callback on attribute select open */
  handleOpen: () => void;
  /** Callback to handle row delete action */
  handleDelete: (attributeId: number, attributeIndex?: number) => void;
  /** Order of the attribute select */
  order: number;
  /** Include or exclude attributes */
  exclude?: boolean;
  /** Is this within a transactional dialog */
  isSubTransactional?: boolean;
  isDependency: boolean;
}

/**
 * Render a single row in the create segment view with select menus for attributes and filtering attributes
 *
 * The select menu value here serves as a button to open a custom dialog which can both set this select and add/remove
 * more AttributeRows and as a display of the row's currently selected attribute. The select menus in all rows have the
 * same options and open the same dialog. Nested groups within each row share their own dialogue with
 */
const AttributeRow = ({
  predicate,
  required,
  handleOpen,
  handleDelete,
  order,
  exclude,
  isSubTransactional,
  isDependency
}: Props) => {
  const { selectedAttributes, dispatch } = useBuildSegment(exclude);
  const attributesQuery = useSegmentAttributes();
  const [groupConversionMenuOpen, setGroupConversionMenuOpen] = React.useState<boolean>(false);

  return (
    <div className={styles.row}>
      <div className={styles.rowInner}>
        {order === 0 && <SegmentCompositionSelector exclude={exclude} isDependency={isDependency} />}
        <div className={styles.controls}>
          {(order === 0 || order !== 1) && (
            <div className={styles.attributeOperatorText}>
              {order === 0 ? (
                <p className={styles.baseAttributeText}>
                  {!isSubTransactional ? (exclude ? 'Exclude' : 'Base Attribute') : ''}
                </p>
              ) : (
                <p className={styles.groupOperatorText}>{SegmentAttributeOperator[selectedAttributes.operator]}</p>
              )}
            </div>
          )}
          {order === 1 && (
            <OperatorSelect
              operator={selectedAttributes.operator}
              handleChange={(operator) => dispatch({ type: 'updateOperator', operator, position: [] })}
            />
          )}
          {predicate === undefined && (
            <Attribute
              selectedAttribute={undefined}
              required={required}
              handleDelete={handleDelete}
              handleOpen={handleOpen}
              handleAddToGroup={() => {}}
              position={[]}
              exclude={exclude}
              attributeIndex={order}
            />
          )}

          {predicate !== undefined &&
            ('attributeId' in predicate ? (
              <React.Fragment>
                <Attribute
                  selectedAttribute={predicate}
                  required={required}
                  handleDelete={handleDelete}
                  handleOpen={handleOpen}
                  handleAddToGroup={() => setGroupConversionMenuOpen(true)}
                  position={[]}
                  exclude={exclude}
                  attributeIndex={order}
                />
                {/* Provisional menu to select attributes when converting a base attribute to a group */}
                <AttributeMenu
                  open={groupConversionMenuOpen}
                  onApply={(checked) => {
                    if (checked.length > 0) {
                      dispatch({
                        type: 'convertAttributeToGroup',
                        attribute: predicate,
                        newGroupAttributeIds: checked,
                        attributes: attributesQuery.data.attributes
                      });
                    }
                  }}
                  onCancel={() => setGroupConversionMenuOpen(false)}
                />
              </React.Fragment>
            ) : (
              <AttributeGroup attributeGroup={predicate} position={[order]} exclude={exclude} />
            ))}
        </div>
        {predicate && <AttributeRowCustomers order={order} exclude={exclude} />}
      </div>
    </div>
  );
};

export default React.memo(AttributeRow);
